import React, { forwardRef, memo, useCallback, useEffect } from "react";
import clsx from "clsx";
import { getColourCodeById, getMotorizationData, getName } from "../../../../utils/gradeUtils";
import { useCarConfigUrl, useGradeImage } from "../../../../utils/gradeHooks";
import { GradeSelectorMemoLabel } from "../../../../utils/labels";
import type { GradeBodyTypeType } from "../../../../utils/types/Model";
import Price from "./price/Price";
import Icon from "../../../../../shared-components/css-modules/lexus/atoms/Icon";
import Text from "../../../../../shared-components/css-modules/lexus/atoms/Text";
import Button from "../../../../../shared-components/css-modules/lexus/atoms/Button";
import styles from "./Item.module.scss";
import { trackSelectColour, trackToCarConfigCTA } from "../../../../utils/tracking";
import a11y from "../../../../../shared-components/css-modules/lexus/utils/Accessibility.module.scss";
import { useGradeSelectorDispatch, useGradeSelectorSelector } from "../../../../redux/hooks";
import { setColour } from "../../../../redux/slices/GradeSelectorModelSlice";
import EcoItems from "./EcoItems";
import { showModal } from "../../../../../../../common-deprecated/redux/actions/ModalActions";
import { MODAL_GRADE_SELECTOR_COMPARE } from "../../../../utils/modalConstants";
import { getUniqueGradeBodyTypeId } from "../../../../../../../common-deprecated/features/apheleia/utils/aphUtils";
import Swatch from "../../../../../shared-components/css-modules/lexus/atoms/Swatch";
import InfoSection from "./info-section/InfoSection";
import Incentive from "../../../../../shared-components/css-modules/lexus/molecules/Incentive";
import { getCarDBAssetUrl } from "../../../../../../shared-logic/utils/imageUtils";
import { useFilterCarExtrasAndPacks } from "../../../../utils/filterHooks";
import Popover from "../../../../../shared-components/css-modules/lexus/vendor/popover/Popover";
import { CommonLabel, getCommonOptionalLabel } from "../../../../../../../common-deprecated/utils/commonLabels";
import SanitizedRenderer from "../../../../../../../common-deprecated/components/SanitizedRenderer";
import { sanitize } from "../../../../../../../common-deprecated/utils/sanitizer";

const Item = forwardRef<
    HTMLDivElement,
    {
        gradeBodyType: GradeBodyTypeType;
    }
>(({ gradeBodyType }, ref) => {
    const dispatch = useGradeSelectorDispatch();

    const commonSettings = useGradeSelectorSelector((state) => state.commonSettings);

    const id = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.id);
    const modelName = useGradeSelectorSelector((state) => state.gradeSelectorModel.model.name);
    const compressedCcisPath = useGradeSelectorSelector((state) => state.commonSettings.compressedCcisPath);

    const { hasMultipleBodyTypes, bodyType, grade, engines } = gradeBodyType;
    const name = getName(gradeBodyType);
    const eta = gradeBodyType.engines[0]?.transmissions[0]?.wheeldrives[0]?.eta;
    const colourCode = getColourCodeById(gradeBodyType);
    const subModelName = gradeBodyType.submodel?.name;
    const { wheeldrive } = getMotorizationData(gradeBodyType);
    const { car } = wheeldrive;
    const carExtrasAndPacks = useFilterCarExtrasAndPacks(car);
    const imgAltDescription = `${name}${subModelName ? ` ${subModelName}` : ""}${` ${bodyType.name}`}`;

    const imageBase = useGradeImage(grade, bodyType, car, 331, 131, 9, undefined, colourCode, carExtrasAndPacks).src;
    const imageBase2x = useGradeImage(
        grade,
        bodyType,
        car,
        331 * 2,
        131 * 2,
        9,
        undefined,
        colourCode,
        carExtrasAndPacks,
    ).src;

    const carConfigUrl = useCarConfigUrl(
        car,
        undefined,
        gradeBodyType?.selectedColourId
            ? {
                  colourId: gradeBodyType.selectedColourId,
                  colourCode,
              }
            : undefined,
        carExtrasAndPacks.extras,
        carExtrasAndPacks.packs,
    );

    const uniqueBodyTypeId = getUniqueGradeBodyTypeId(grade.id, gradeBodyType.bodyType.id, gradeBodyType.submodel?.id);
    const carGradeName = `${modelName} ${gradeBodyType.grade.name} ${gradeBodyType.bodyType.name}`;

    const onCTAClick = useCallback(() => {
        trackToCarConfigCTA(carGradeName);
    }, [carGradeName]);

    useEffect(() => {
        if (!wheeldrive.colors?.length) return;

        const { selectedColourId } = gradeBodyType;
        const isColorValid = selectedColourId && wheeldrive.colors.some((color) => color.id === selectedColourId);

        if (!isColorValid) {
            dispatch(
                setColour({
                    uniqueBodyTypeId,
                    colourId: wheeldrive.colors[0].id,
                }),
            );
        }
    }, [wheeldrive.colors, gradeBodyType.selectedColourId, uniqueBodyTypeId, dispatch]);

    const etaTooltip = getCommonOptionalLabel({ commonSettings }, "etaTooltip");

    return (
        <article className={styles.wrapper} ref={ref}>
            {/* Header, name, ecotag, ... */}
            <section className={styles.section}>
                <header className={styles.header}>
                    <div>
                        <div className={styles["pre-title"]}>
                            <EcoItems
                                modelId={id}
                                engines={engines}
                                limit={2}
                                disable={{ efficiencyLabel: true }}
                                hasAdditionalIncentives={!!gradeBodyType.bestSellerLabel}
                            />

                            {gradeBodyType.bestSellerLabel && (
                                <Incentive label={gradeBodyType.bestSellerLabel} variant="best-seller" />
                            )}
                        </div>
                        <div className={styles.title}>
                            <Text tag="h3" variant="apheleia-h4">
                                {name}
                            </Text>
                            {subModelName && (
                                <Text tag="h4" variant="apheleia-h6" className={styles["sub-model-name"]}>
                                    {subModelName}
                                </Text>
                            )}
                        </div>

                        {hasMultipleBodyTypes && (
                            <Text tag="h4" variant="apheleia-body-large">
                                {bodyType.name}
                            </Text>
                        )}
                    </div>
                </header>
            </section>

            {/* Image */}
            <section className={clsx(styles.section, styles["image-section"])}>
                <div>
                    <a href={carConfigUrl} onClick={onCTAClick}>
                        <figure className={styles.image}>
                            <img
                                src={imageBase}
                                srcSet={`${imageBase} 1x, ${imageBase2x} 2x`}
                                width="331"
                                height="131"
                                loading="lazy"
                                alt={imgAltDescription}
                            />
                        </figure>
                    </a>
                </div>
            </section>

            {/* Colors */}
            <section className={styles.section}>
                <div className={styles.colors}>
                    {wheeldrive.colors.map((colour, index) => (
                        <Button
                            variant="swatch"
                            key={colour.id}
                            className={styles.swatch}
                            onClick={() => {
                                trackSelectColour(colour.name);
                                dispatch(
                                    setColour({
                                        uniqueBodyTypeId,
                                        colourId: colour.id,
                                    }),
                                );
                            }}
                        >
                            <Swatch
                                className={styles.swatch}
                                image={getCarDBAssetUrl(colour.asset, compressedCcisPath, 48, 48)}
                                fill={colour.rgb}
                                active={
                                    (!gradeBodyType.selectedColourId && index === 0) ||
                                    colour.id === gradeBodyType.selectedColourId
                                }
                            />
                            <span className={a11y["sr-only"]}>{colour.name}</span>
                        </Button>
                    ))}
                </div>
            </section>

            {/* Price & ETA */}
            <section className={styles.section}>
                <div className={styles.price}>
                    <Price
                        wheeldrive={wheeldrive}
                        selectedColourId={gradeBodyType.selectedColourId}
                        showPromotionsNumbers
                        column
                    />
                </div>
                {eta && (
                    <div className={styles.eta}>
                        <p>{eta}</p>
                        {etaTooltip && (
                            <Popover placement="top">
                                <Popover.Trigger>
                                    <Button
                                        onClick={(event) => {
                                            event.preventDefault();
                                            event.stopPropagation();
                                        }}
                                        variant="clean"
                                        type="button"
                                        className={styles.info}
                                    >
                                        <Icon variant="info" sizing="md" aria-hidden="true" />
                                        <span className={a11y["sr-only"]}>
                                            <CommonLabel label="ariaToggleEtaTooltip" />
                                        </span>
                                    </Button>
                                </Popover.Trigger>
                                <Popover.Content>
                                    <SanitizedRenderer html={sanitize(etaTooltip)} />
                                </Popover.Content>
                            </Popover>
                        )}
                    </div>
                )}
            </section>

            {/* CTA's */}
            <section className={styles.section}>
                <div className={styles.buttongroup}>
                    <Button
                        data-testid="grade-selector-cta-to-car-config"
                        type="a"
                        href={carConfigUrl}
                        onClick={onCTAClick}
                        variant="btn-default"
                        sizing="full-width"
                        className={styles.button}
                    >
                        <GradeSelectorMemoLabel label="gradeSelectorGradeCtaButton" />
                        <span className={a11y["sr-only"]}>{name}</span>
                        <Icon variant="arrow-long-right" sizing="sm" aria-hidden="true" />
                    </Button>
                    <Button
                        type="button"
                        onClick={() =>
                            dispatch(
                                showModal({
                                    type: MODAL_GRADE_SELECTOR_COMPARE,
                                    models: [
                                        {
                                            gradeId: gradeBodyType.grade.id,
                                            submodelId: gradeBodyType.submodel?.id,
                                            colourId: gradeBodyType.selectedColourId,
                                            motorization: gradeBodyType.selectedMotorization,
                                        },
                                    ],
                                }),
                            )
                        }
                        variant="btn-secondary"
                        sizing="full-width"
                        className={styles.button}
                    >
                        <GradeSelectorMemoLabel label="gradeSelectorCompare" />
                        <Icon variant="compare" sizing="md" aria-hidden="true" />
                    </Button>
                </div>
            </section>

            <InfoSection gradeBodyType={gradeBodyType} />
        </article>
    );
});

export default memo(Item);
